import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { assistantService } from '../api/services/assistant.service'
import { caseService } from '../api/services/case.service'
import { doctorService } from '../api/services/doctor.service'
import { AppShell } from '../components/layout/AppShell'
import {
  buildCasePayload,
  CaseAllowNationalCommentsField,
  CaseAssistantReviewField,
  CaseFormFields,
  CaseNationalHubField,
  emptyCaseForm,
  useCaseForm,
} from '../components/cases/CaseFormFields'
import {
  buildCaseFilesFormData,
  CaseMediaUploader,
  type PendingCaseMedia,
} from '../components/cases/CaseMediaUploader'
import { Button } from '../components/ui/button'
import type { SelectOption } from '../components/ui/select-picker'
import { useAuth } from '../context/AuthContext'
import { USER_ROLES } from '../types/auth'
import type { CaseFiltersResponse } from '../types/case'

export function CaseCreatePage() {
  const navigate = useNavigate()
  const { session } = useAuth()
  const role = session?.role ?? null
  const [filters, setFilters] = useState<CaseFiltersResponse | null>(null)
  const [doctorOptions, setDoctorOptions] = useState<SelectOption[]>([])
  const [pendingMedia, setPendingMedia] = useState<PendingCaseMedia[]>([])
  const [submitting, setSubmitting] = useState(false)

  const form = useCaseForm(emptyCaseForm)

  const showDoctorPicker = role === USER_ROLES.ASSISTANT || role === USER_ROLES.SUPER_ADMIN
  const consultantReadOnly = role === USER_ROLES.DOCTOR

  const canSetGlobal = useMemo(() => {
    if (role === USER_ROLES.ASSISTANT) {
      return false
    }

    if (role === USER_ROLES.SUPER_ADMIN || role === USER_ROLES.DOCTOR) {
      return true
    }

    return false
  }, [role])

  const canOpenForAssistantReview =
    role === USER_ROLES.DOCTOR || role === USER_ROLES.SUPER_ADMIN

  useEffect(() => {
    if (!canSetGlobal) {
      form.setValue('is_global', false)
    }
  }, [canSetGlobal, form])

  useEffect(() => {
    void caseService.filters().then((response) => setFilters(response.data))
  }, [])

  useEffect(() => {
    if (role === USER_ROLES.DOCTOR && session?.user.id) {
      form.setValue('belongs_to_doctor_id', session.user.id, { shouldValidate: true })
      return
    }

    if (role === USER_ROLES.ASSISTANT) {
      void assistantService.doctorPicker().then((response) => {
        const options = (response.data ?? [])
          .map((link) => {
            const doctor = link.doctor
            if (!doctor) {
              return null
            }

            return {
              value: doctor.id,
              label: `${doctor.first_name} ${doctor.surname}`.trim() || doctor.email,
            }
          })
          .filter((option): option is SelectOption => option !== null)

        setDoctorOptions(options)
      })
      return
    }

    if (role === USER_ROLES.SUPER_ADMIN) {
      void doctorService.list({ status: 'approved' }).then((response) => {
        const options = (Array.isArray(response.data) ? response.data : []).map((doctor) => ({
          value: doctor.id,
          label: `${doctor.first_name} ${doctor.surname}`.trim() || doctor.email,
        }))
        setDoctorOptions(options)
      })
    }
  }, [form, role, session?.user.id])

  const doctorPickerReady = useMemo(() => {
    if (!showDoctorPicker) {
      return true
    }

    return doctorOptions.length > 0
  }, [doctorOptions.length, showDoctorPicker])

  const onSubmit = async (values: typeof emptyCaseForm) => {
    setSubmitting(true)

    try {
      const response = await caseService.create(buildCasePayload(values))
      const caseId = response.data.id

      if (pendingMedia.length > 0) {
        await caseService.uploadFiles(caseId, buildCaseFilesFormData(pendingMedia))
      }

      navigate(`/cases/${caseId}`)
    } catch {
      // handled globally
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-2 py-2">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-bold">Upload a New Case</h1>
          <p className="mt-1 text-sm text-ink-600">
            Create a draft case.
            {role === USER_ROLES.ASSISTANT
              ? ' Assistants save drafts only — a doctor publishes or schedules later. Choose the consultant doctor; you will be recorded as the uploader.'
              : ' You can publish and open review after saving. You are recorded as the uploader.'}
          </p>
        </div>

        <section className="rounded-lg border border-parch-100 bg-white p-6 shadow-card">
          <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
            <CaseFormFields
              form={form}
              filters={filters}
              doctorOptions={doctorOptions}
              showDoctorPicker={showDoctorPicker}
              consultantReadOnly={consultantReadOnly}
              afterDescription={
                <CaseMediaUploader
                  files={pendingMedia}
                  onChange={setPendingMedia}
                  disabled={submitting}
                  title="Case images / files (optional)"
                />
              }
            />

            <div className="space-y-3">
              <div
                className={`grid items-stretch gap-4 ${
                  canSetGlobal && canOpenForAssistantReview
                    ? 'md:grid-cols-2'
                    : 'grid-cols-1'
                }`}
              >
                <CaseNationalHubField
                  form={form}
                  disabled={submitting}
                  canSetGlobal={canSetGlobal}
                />
                <CaseAssistantReviewField
                  form={form}
                  disabled={submitting}
                  visible={canOpenForAssistantReview}
                />
              </div>
              {canSetGlobal ? (
                <CaseAllowNationalCommentsField form={form} disabled={submitting} />
              ) : null}
            </div>

            <div className="flex justify-end gap-2 border-t border-parch-100 pt-4">
              <Button type="button" variant="secondary" asChild>
                <Link to="/cases">Cancel</Link>
              </Button>
              <Button type="submit" disabled={submitting || (showDoctorPicker && !doctorPickerReady)}>
                {submitting ? 'Creating…' : 'Create Case'}
              </Button>
            </div>
          </form>
        </section>
      </div>
    </AppShell>
  )
}
